import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Dialog, Divider, IconButton, Menu, MenuItem, Stack, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon, MoreHoriz as MoreIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../../../theme/tokens';
import { ConfirmDialog, InlineMessage, LoadingState } from '../../ui';
import { useToast } from '../../../contexts/ToastContext';
import { useUnsavedChangesSource } from '../../appUpdate';
import { getVendorInfo } from '../../../utils/vendorLogos';
import { CompatibilityEdgesPanel, fetchCompatibilityEdges } from '../CompatibilityEdgesPanel';
import { Dot, Pill, TabBody } from './primitives';
import { productDetailApi } from './productDetailApi';
import type { AdminCanonicalProduct, AdminProductDetail, RepairToolId, ToolResult } from './productDetailTypes';
import { BLANK_SUBMISSION_PRODUCT, buildPatch, draftFromProduct, DraftSubmission, DraftValidationError, ProductDraft, submissionFromDraft, submitterPatch } from './productDraft';
import { productSubmissionsApi, type ProductSubmissionScope } from '../../../services/productSubmissionsApi';
import { formatDate, formatDateTime, money } from './productDetailFormat';
import type { ProductLifecycleInput } from '../ProductLifecycleEditor';
import { RepairEnrichCard, REPAIR_TOOLS, SessionLogEntry, Suggestion } from './RepairEnrichCard';
import { isNotFoundStatus, PERMALINK_STALE_MS, statusOfError } from '../../../hooks/useProductPermalink';
import { StagedUpdateBanner } from './StagedUpdateBanner';
import { LensFocalRangePanel } from './LensFocalRangePanel';
import { OverviewTab, statusTone } from './tabs/OverviewTab';
import { ContentTab } from './tabs/ContentTab';
import { MediaTab } from './tabs/MediaTab';
import { SpecsTab } from './tabs/SpecsTab';
import { PricingTab } from './tabs/PricingTab';
import { SourceHistoryTab } from './tabs/SourceHistoryTab';

type TabId = 'overview' | 'content' | 'media' | 'specs' | 'pricing' | 'source' | 'compat';
const SUBMITTER_TABS: TabId[] = ['overview', 'content', 'media', 'specs'];

interface Props {
  productId: string | null;
  /** The list row, shown while the full detail loads. */
  initialProduct?: AdminCanonicalProduct | null;
  skuHint?: string;
  /**
   * Direct links are on (flag `product_permalinks`), for every open, from a row or from the
   * address: the by-id read is kept for `PERMALINK_STALE_MS` (an edit made here invalidates it),
   * is not retried when the server says the product is not there, and that case reads "not found"
   * instead of offering a retry.
   */
  permalink?: boolean;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
  /** Opens another canonical product in this same dialog (a variant sibling). */
  onOpenProduct?: (next: { id: string; sku?: string; name?: string }) => void;
  /**
   * A store or vendor viewing and editing its own submitted product (flag
   * `product_submissions`, MOT-695): read and saved through the submission
   * routes, with Motzu's repair tools, pricing, source history, status,
   * lifecycle and delete left out. `productId` is the provisional product.
   */
  submitter?: { scope: ProductSubmissionScope; submissionId: string; statusLabel: string };
  /**
   * A store or vendor submitting a new product (flag `product_submissions`,
   * MOT-701): the submitter's editor on a blank product, open for editing from
   * the start, with brand, its SKU and the MSRP / MAP it knows editable too.
   * `onSubmit` gets the draft as a submission and rejects with the reason to
   * show when it was not accepted; the caller closes the dialog on success.
   * `productId` is ignored.
   */
  create?: {
    submitLabel: string;
    /** Under the Prices card: where the MSRP and MAP go for this submitter. */
    priceNote: string;
    /** Under the SKU field: what this submitter's SKU is (a store's own, a vendor's product SKU). */
    skuHint: string;
    /** Shown above the tabs, e.g. what a private save leaves out. */
    notice?: React.ReactNode;
    onSubmit: (submission: DraftSubmission) => Promise<void>;
  };
}

const EMPTY_RELATED = { priceHistoryCount: 0, storeOverrideCount: 0, organizationPricingCount: 0, rebates: [] };

const blankDraft = () => draftFromProduct(BLANK_SUBMISSION_PRODUCT);

const errorText = (error: unknown) => (error instanceof Error ? error.message : 'Request failed');

/**
 * Admin product detail (design_handoff_admin_product_detail, v2): the whole
 * canonical document in seven tabs, data problems flagged where they live,
 * one-click fixes and repair tools that only suggest. Edits collect in one
 * draft across tabs and save in one PUT; saved fields are locked as manual so a
 * staged promotion keeps them.
 */
export const AdminProductDetailDialog: React.FC<Props> = ({ productId, initialProduct, permalink, onClose, onChanged, onDeleted, onOpenProduct, submitter, create }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const toast = useCallback((message: string) => showToast(message, 'success'), [showToast]);
  const open = !!productId || !!create;
  const creating = !!create;
  // The submitter's view of the editor: a submission being edited, or one being created.
  const submitterView = !!submitter || creating;

  const [tab, setTab] = useState<TabId>('overview');
  const [editing, setEditing] = useState(creating);
  const [base, setBase] = useState<ProductDraft | null>(() => (creating ? blankDraft() : null));
  const [draft, setDraft] = useState<ProductDraft | null>(() => (creating ? blankDraft() : null));
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [running, setRunning] = useState<Partial<Record<RepairToolId, boolean>>>({});
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [log, setLog] = useState<SessionLogEntry[]>([]);
  const [lastVerify, setLastVerify] = useState<string | undefined>();
  const [saveError, setSaveError] = useState('');
  const [lifecyclePending, setLifecyclePending] = useState<ProductLifecycleInput | null | undefined>(undefined);
  const [lifecycleInvalid, setLifecycleInvalid] = useState(false);
  const logId = useRef(0);

  // Reset per product. A new product opens straight into editing.
  useEffect(() => {
    setTab('overview');
    setEditing(creating);
    setBase(creating ? blankDraft() : null);
    setDraft(creating ? blankDraft() : null);
    setSuggestions([]);
    setLog([]);
    setRunning({});
    setLastVerify(undefined);
    setSaveError('');
    setLifecyclePending(undefined);
    setLifecycleInvalid(false);
  }, [productId, creating]);

  const queryKey = useMemo(
    () => (submitter ? ['submitted-product-detail', submitter.scope, submitter.submissionId] : ['admin-product-detail', productId]),
    [productId, submitter],
  );
  const detail = useQuery<AdminProductDetail>({
    queryKey,
    queryFn: async () => {
      if (!submitter) return productDetailApi.detail(productId as string);
      const { product: submitted } = await productSubmissionsApi.product(submitter.scope, submitter.submissionId);
      return { product: submitted, issues: { classification: null, duplicateSpecs: null, priceCheck: null }, stagedUpdate: null, related: EMPTY_RELATED };
    },
    enabled: open && !creating,
    ...(permalink
      ? {
          staleTime: PERMALINK_STALE_MS,
          // The app's one retry stays for a blip; a product the server says is not there is not retried.
          retry: (failureCount: number, error: unknown) => failureCount < 1 && !isNotFoundStatus(statusOfError(error)),
        }
      : {}),
  });
  const compat = useQuery({
    queryKey: ['admin-compatibility-edges', productId],
    enabled: open && !submitterView,
    queryFn: () => fetchCompatibilityEdges(productId as string),
  });

  const product = creating ? BLANK_SUBMISSION_PRODUCT : detail.data?.product ?? initialProduct ?? null;
  const viewDraft = useMemo(() => (product ? draftFromProduct(product) : null), [product]);
  const current = editing && draft ? draft : viewDraft;
  const stored = editing && base ? base : viewDraft;
  const draftDirty = !!(editing && draft && base && JSON.stringify(draft) !== JSON.stringify(base));
  const dirty = draftDirty || lifecyclePending !== undefined;
  const editedPaths = useMemo(() => {
    if (!dirty || !product || !draft || !base) return [];
    try {
      return buildPatch(product, base, draft).changed;
    } catch {
      return [];
    }
  }, [dirty, product, draft, base]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
    onChanged();
  }, [queryClient, queryKey, onChanged]);

  const addLog = useCallback((text: string, tone: SessionLogEntry['tone'], href?: string) => {
    logId.current += 1;
    setLog((prev) => [{ id: logId.current, text, tone, href }, ...prev]);
  }, []);

  const startEditing = useCallback(() => {
    if (!product || editing) return;
    const next = draftFromProduct(product);
    setBase(next);
    setDraft(JSON.parse(JSON.stringify(next)));
    setEditing(true);
    setMenuAnchor(null);
  }, [product, editing]);

  const update = useCallback((patch: Partial<ProductDraft>) => setDraft((prev) => (prev ? { ...prev, ...patch } : prev)), []);

  const discard = useCallback(() => {
    setEditing(creating);
    setBase(creating ? blankDraft() : null);
    setDraft(creating ? blankDraft() : null);
    setSaveError('');
    setLifecyclePending(undefined);
    setLifecycleInvalid(false);
  }, [creating]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!create || !draft) return;
      await create.onSubmit(submissionFromDraft(draft));
    },
    onError: (error) => setSaveError(error instanceof DraftValidationError ? error.message : errorText(error)),
  });
  const canSubmit = !!draft && !!draft.brand.trim() && !!draft.model.trim() && !submitMutation.isPending;

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!product || !draft || !base) return null;
      const { payload, changed } = buildPatch(product, base, draft);
      if (lifecyclePending !== undefined) payload.lifecycle = lifecyclePending;
      if (changed.length === 0 && lifecyclePending === undefined) return null;
      if (submitter) {
        const allowed = submitterPatch(payload);
        if (allowed.dropped.length) throw new DraftValidationError(`These fields cannot be changed here: ${allowed.dropped.join(', ')}`);
        return (await productSubmissionsApi.updateProduct(submitter.scope, submitter.submissionId, allowed.payload)).product;
      }
      return productDetailApi.update(product.id, payload, 'manual');
    },
    onSuccess: (saved) => {
      discard();
      if (saved) {
        toast(submitter ? 'Changes saved. Motzu reviews them with your submission.' : 'Changes saved. Saved fields are marked manual.');
        refresh();
      }
    },
    onError: (error) => setSaveError(error instanceof DraftValidationError ? error.message : errorText(error)),
  });

  const save = useCallback(() => {
    if (lifecycleInvalid) {
      setSaveError('Fix the lifecycle dates before saving. Those edits stay in the form.');
      return Promise.resolve();
    }
    return creating ? submitMutation.mutateAsync() : saveMutation.mutateAsync();
  }, [creating, lifecycleInvalid, submitMutation, saveMutation]);

  // MAP is the advertised floor stores push, and usually comes from a price sheet:
  // changing or clearing a stored MAP asks first.
  // The values stay set while the dialog animates closed, so its copy does not flip.
  const [mapConfirm, setMapConfirm] = useState<{ from: string; to: string } | null>(null);
  const [mapConfirmOpen, setMapConfirmOpen] = useState(false);
  const requestSave = () => {
    if (lifecycleInvalid) {
      setSaveError('Fix the lifecycle dates before saving. Those edits stay in the form.');
      return;
    }
    if (product && draft && base && base.map) {
      try {
        const { payload, changed } = buildPatch(product, base, draft);
        if (changed.includes('pricing.map')) {
          const next = (payload.pricing as Record<string, unknown> | undefined)?.map;
          setMapConfirm({ from: base.map, to: next == null ? '' : String(next) });
          setMapConfirmOpen(true);
          return;
        }
      } catch {
        // An invalid draft fails the save itself, which reports the reason.
      }
    }
    saveMutation.mutate();
  };
  useUnsavedChangesSource({ dirty, save, discard });

  const fixMutation = useMutation({
    mutationFn: ({ payload }: { payload: Record<string, unknown>; message: string }) => productDetailApi.update(productId as string, payload, 'manual'),
    onSuccess: (_d, vars) => {
      toast(vars.message);
      addLog(vars.message, 'positive');
      refresh();
    },
    onError: (error) => showToast(errorText(error), 'error'),
  });
  const applyFix = useCallback(async (payload: Record<string, unknown>, message: string) => {
    await fixMutation.mutateAsync({ payload, message }).catch(() => undefined);
  }, [fixMutation]);

  const handleToolResult = useCallback((result: ToolResult) => {
    if (result.kind === 'suggestions') {
      if (result.rows.length === 0) {
        addLog(`${REPAIR_TOOLS.find((t) => t.id === result.tool)?.label ?? result.tool}: nothing to change`, 'neutral');
        return;
      }
      setSuggestions((prev) => [result, ...prev.filter((s) => s.tool !== result.tool)]);
      return;
    }
    if (result.kind === 'info') {
      addLog(result.message, result.refresh ? 'positive' : 'neutral');
      if (result.refresh) refresh();
      return;
    }
    addLog(result.message, 'neutral', result.href);
  }, [addLog, refresh]);

  const runTool = useCallback(async (tool: RepairToolId) => {
    if (!productId || running[tool]) return;
    setRunning((r) => ({ ...r, [tool]: true }));
    try {
      const result = await productDetailApi.runTool(productId, tool);
      if (tool === 'verify') setLastVerify(formatDateTime(new Date().toISOString()));
      handleToolResult(result);
    } catch (error) {
      showToast(errorText(error), 'error');
    } finally {
      setRunning((r) => ({ ...r, [tool]: false }));
    }
  }, [productId, running, handleToolResult, showToast]);

  const acceptMutation = useMutation({
    mutationFn: async (tools: RepairToolId[]) => {
      const picked = suggestions.filter((s) => tools.includes(s.tool));
      const payload: Record<string, unknown> = {};
      for (const s of picked) {
        for (const [key, value] of Object.entries(s.patch)) {
          if (key === 'pricing' && value && typeof value === 'object') {
            payload.pricing = { ...((payload.pricing as object) || {}), ...(value as object) };
          } else {
            payload[key] = value;
          }
        }
      }
      await productDetailApi.update(productId as string, payload, 'ai');
      return picked;
    },
    onSuccess: (picked) => {
      setSuggestions((prev) => prev.filter((s) => !picked.some((p) => p.tool === s.tool)));
      for (const s of picked) addLog(`${s.title}: accepted`, 'positive');
      toast(picked.length === 1 ? 'Suggestion saved as AI' : `${picked.length} suggestions saved as AI`);
      refresh();
    },
    onError: (error) => showToast(errorText(error), 'error'),
  });

  const releaseMutation = useMutation({
    mutationFn: (paths: string[]) => productDetailApi.releaseManual(productId as string, paths),
    onSuccess: (_d, paths) => {
      toast(paths.length === 1 ? `${paths[0]} released; the next staged update may change it` : `${paths.length} fields released`);
      refresh();
    },
    onError: (error) => showToast(errorText(error), 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => productDetailApi.remove(productId as string),
    onSuccess: () => {
      setConfirmDelete(false);
      toast('Product deleted');
      onDeleted();
    },
    onError: (error) => showToast(errorText(error), 'error'),
  });

  const requestClose = () => {
    if (dirty) setConfirmClose(true);
    else onClose();
  };

  const issues = detail.data?.issues;
  const issueLines = [
    issues?.classification && `type fields disagree (${issues.classification.field} is ${issues.classification.actual}, expected ${issues.classification.expected})`,
    issues?.duplicateSpecs && 'specs are stored twice',
    issues?.priceCheck && `price check has failed${issues.priceCheck.failCount ? ` ${issues.priceCheck.failCount} time${issues.priceCheck.failCount === 1 ? '' : 's'}` : ''} (retry it on Pricing)`,
  ].filter(Boolean) as string[];
  const recommended: RepairToolId[] = [issues?.classification ? 'verify' : null, issues?.duplicateSpecs ? 'scrape' : null].filter(Boolean) as RepairToolId[];

  const mediaCount = current ? current.images.length + current.videos.length + current.documentation.length : 0;
  const specCount = current ? current.specGroups.reduce((n, g) => n + g.rows.length, 0) : 0;
  const allTabs: Array<{ id: TabId; label: string; count?: number; dot?: boolean }> = [
    { id: 'overview', label: 'Overview', dot: !!issues?.classification },
    { id: 'content', label: 'Content' },
    { id: 'media', label: 'Media', count: mediaCount },
    { id: 'specs', label: 'Specs', count: specCount, dot: !!issues?.duplicateSpecs },
    { id: 'pricing', label: 'Pricing', dot: !!issues?.priceCheck },
    { id: 'source', label: 'Source & history' },
    { id: 'compat', label: 'Compatibility', count: compat.data?.length || undefined },
  ];
  // A submitter edits the content it knows; pricing, sources and compatibility are Motzu's.
  const tabs = submitterView ? allTabs.filter((t) => SUBMITTER_TABS.includes(t.id)) : allTabs;

  const vendor = product?.brand ? getVendorInfo(product.brand) : null;
  const currency = product?.pricing?.currency || 'USD';

  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose}
        fullScreen={fullScreen}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 1180,
            height: fullScreen ? '100%' : 'calc(100vh - 64px)',
            minHeight: fullScreen ? undefined : 640,
            borderRadius: fullScreen ? 0 : `${tokens.radius.panel}px`,
            boxShadow: tokens.shadow.overlay,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        {permalink && detail.isError && isNotFoundStatus(statusOfError(detail.error)) ? (
          // Wins over a list-row seed or earlier cached detail: a product the server will not serve is not shown.
          <Box sx={{ p: 3 }}>
            <InlineMessage severity="warning">That product was not found, or you do not have access to it.</InlineMessage>
            <Button onClick={onClose}>Back to the list</Button>
          </Box>
        ) : !product || !current || !stored ? (
          detail.isError ? (
            <Box sx={{ p: 3 }}>
              <InlineMessage severity="error">{errorText(detail.error)}</InlineMessage>
              <Button onClick={() => detail.refetch()}>Try again</Button>
            </Box>
          ) : (
            <LoadingState size="page" message="Loading product…" />
          )
        ) : (
          <>
            {/* Header */}
            <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-start', gap: 2, px: '22px', pt: '18px', flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 360px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                <Stack direction="row" alignItems="center" spacing={1.1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                  {vendor?.logo ? (
                    <Box component="img" src={vendor.logo} alt={product.brand} sx={{ height: 11, maxWidth: 52, objectFit: 'contain' }} />
                  ) : (
                    <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: tokens.ink.bodyAlt }}>{product.brand}</Typography>
                  )}
                  <Box component="span" sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: tokens.ink.disabled }} />
                  <Typography component="span" sx={{ fontSize: 12, color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>SKU {current.sku || product.sku || '—'}</Typography>
                  {creating ? (
                    <Pill tone="info">New submission</Pill>
                  ) : submitter ? (
                    <>
                      <Pill tone="info">{submitter.statusLabel}</Pill>
                      <Pill tone="neutral" dot={false}>Submitted by you</Pill>
                    </>
                  ) : (
                    <>
                      <Pill tone={statusTone(product.status)}>{product.status ? product.status.charAt(0).toUpperCase() + product.status.slice(1) : 'Unknown'}</Pill>
                      <Pill tone="neutral" dot={false}>Canonical</Pill>
                    </>
                  )}
                </Stack>
                <Typography component="h2" sx={{ fontSize: 19, fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.01em', overflowWrap: 'anywhere' }}>
                  {current.name || product.name || (creating ? 'New product' : '')}
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: '0 0 auto' }}>
                {dirty && !creating && (
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Dot tone="caution" />
                    <Typography component="span" sx={{ fontSize: 12, color: tokens.signal.caution.ink }}>Unsaved changes</Typography>
                  </Stack>
                )}
                {!creating && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon sx={{ fontSize: '14px !important' }} />}
                  onClick={startEditing}
                  aria-pressed={editing}
                  sx={{
                    height: 34,
                    borderRadius: '9px',
                    fontSize: 13,
                    fontWeight: 600,
                    ...(editing
                      ? { bgcolor: tokens.accent.tint, color: tokens.accent.hover, borderColor: tokens.accent.border }
                      : { color: tokens.ink.primary, borderColor: tokens.line.default }),
                  }}
                >
                  {editing ? 'Editing' : 'Edit'}
                </Button>
                )}
                {!submitterView && (
                  <IconButton
                    aria-label="More actions"
                    onClick={(e) => setMenuAnchor(e.currentTarget)}
                    sx={{ width: 34, height: 34, border: `1px solid ${tokens.line.default}`, borderRadius: '9px' }}
                  >
                    <MoreIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton aria-label="Close" onClick={requestClose} sx={{ width: 32, height: 32, borderRadius: '9px' }}>
                  <CloseIcon sx={{ fontSize: 16, color: tokens.ink.muted }} />
                </IconButton>
              </Stack>
              <Menu
                anchorEl={menuAnchor}
                open={!!menuAnchor}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { width: 230, borderRadius: `${tokens.radius.card}px`, boxShadow: tokens.shadow.menu, mt: 0.75 } } }}
              >
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    navigate(`/app/products?search=${encodeURIComponent(product.sku || product.name || product.id)}`);
                  }}
                >
                  Open in store product browser
                </MenuItem>
                <MenuItem
                  onClick={async () => {
                    setMenuAnchor(null);
                    try {
                      await navigator.clipboard.writeText(product.id);
                      toast('Product ID copied');
                    } catch {
                      showToast('Copy failed: the browser blocked clipboard access', 'error');
                    }
                  }}
                >
                  Copy product ID
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    setConfirmDelete(true);
                  }}
                  sx={{ fontWeight: 600, color: tokens.signal.problem.ink, '&:hover': { bgcolor: tokens.signal.problem.tint } }}
                >
                  Delete product…
                </MenuItem>
              </Menu>
            </Box>

            {create?.notice && <Box sx={{ flex: '0 0 auto', px: '22px', pt: 1.5 }}>{create.notice}</Box>}

            {detail.data?.stagedUpdate && (
              <StagedUpdateBanner staged={detail.data.stagedUpdate} editing={editing} editedPaths={editedPaths} currency={currency} />
            )}

            {/* Tabs */}
            <Box sx={{ flex: '0 0 auto', px: '22px', pt: '14px', borderBottom: `1px solid ${tokens.line.default}` }}>
              <Tabs
                value={tab}
                onChange={(_e, next: TabId) => setTab(next)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Product sections"
                sx={{
                  minHeight: 0,
                  '& .MuiTabs-indicator': { height: 2, bgcolor: tokens.accent.main },
                  '& .MuiTab-root': { minHeight: 0, minWidth: 0, px: 1.5, py: '9px', textTransform: 'none', fontSize: 13.5, fontWeight: 500, color: tokens.ink.muted },
                  '& .MuiTab-root.Mui-selected': { fontWeight: 600, color: tokens.ink.primary },
                }}
              >
                {tabs.map((t) => (
                  <Tab
                    key={t.id}
                    value={t.id}
                    label={
                      <Stack direction="row" alignItems="center" spacing={0.9}>
                        <span>{t.label}</span>
                        {t.count != null && t.count > 0 && <Typography component="span" sx={{ fontSize: 12, color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{t.count}</Typography>}
                        {t.dot && <Dot tone="caution" />}
                      </Stack>
                    }
                  />
                ))}
              </Tabs>
            </Box>

            {/* Body */}
            <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', bgcolor: tokens.surface.subtle }}>
              {detail.isError && (
                <Box sx={{ px: '22px', pt: 2 }}>
                  <InlineMessage severity="warning">
                    Could not load checks, staged updates and related records: {errorText(detail.error)}.{' '}
                    <Button size="small" onClick={() => detail.refetch()}>Retry</Button>
                  </InlineMessage>
                </Box>
              )}
              {tab === 'overview' && (
                <OverviewTab
                  product={product}
                  draft={current}
                  base={stored}
                  editing={editing}
                  update={update}
                  classification={issues?.classification ?? null}
                  priceCheck={issues?.priceCheck ?? null}
                  applyFix={applyFix}
                  fixing={fixMutation.isPending}
                  goTo={setTab}
                  onOpenProduct={onOpenProduct}
                  saveLifecycle={async (input) => {
                    await productDetailApi.update(product.id, { lifecycle: input });
                    toast(input ? 'Lifecycle saved' : 'Lifecycle cleared');
                    refresh();
                  }}
                  onLifecyclePending={setLifecyclePending}
                  onLifecycleInvalid={setLifecycleInvalid}
                  submitter={submitterView}
                  creating={create ? { priceNote: create.priceNote, skuHint: create.skuHint } : undefined}
                  repairCard={
                    submitterView ? null : (
                    <RepairEnrichCard
                      issues={issueLines}
                      recommended={recommended}
                      running={running}
                      suggestions={suggestions}
                      log={log}
                      lastVerify={lastVerify ?? (product?.lastAiVerify?.at ? formatDateTime(product.lastAiVerify.at) || undefined : undefined)}
                      editing={editing}
                      accepting={acceptMutation.isPending}
                      onRun={runTool}
                      onAccept={(tools) => acceptMutation.mutate(tools)}
                      onDismiss={(tools) => setSuggestions((prev) => prev.filter((s) => !tools.includes(s.tool)))}
                    />
                    )
                  }
                />
              )}
              {tab === 'content' && (
                <ContentTab product={product} draft={current} base={stored} editing={editing} update={update} startEditing={startEditing} toast={toast} submitter={submitterView} />
              )}
              {tab === 'media' && <MediaTab draft={current} editing={editing} update={update} startEditing={startEditing} toast={toast} />}
              {tab === 'specs' && product.productType === 'lens' && !submitterView && (
                <Box sx={{ px: '22px', pt: 2 }}>
                  <LensFocalRangePanel productId={product.id} />
                </Box>
              )}
              {tab === 'specs' && (
                <SpecsTab
                  product={product}
                  draft={current}
                  base={stored}
                  editing={editing}
                  update={update}
                  duplicateSpecs={issues?.duplicateSpecs ?? null}
                  physicalFromSpecs={detail.data?.physicalFromSpecs}
                  applyFix={applyFix}
                  fixing={fixMutation.isPending}
                />
              )}
              {tab === 'pricing' && (
                <PricingTab
                  product={product}
                  draft={current}
                  base={stored}
                  editing={editing}
                  update={update}
                  priceCheck={issues?.priceCheck ?? null}
                  related={detail.data?.related ?? { priceHistoryCount: 0, storeOverrideCount: 0, organizationPricingCount: 0, rebates: [] }}
                  runningTools={running as Record<string, boolean>}
                  runTool={runTool}
                />
              )}
              {tab === 'source' && (
                <SourceHistoryTab
                  product={product}
                  draft={current}
                  editing={editing}
                  update={update}
                  releaseLocks={(paths) => releaseMutation.mutate(paths)}
                  releasing={releaseMutation.isPending}
                  toast={toast}
                />
              )}
              {tab === 'compat' && (
                <TabBody>
                  <CompatibilityEdgesPanel productId={product.id} />
                </TabBody>
              )}
            </Box>

            {/* Footer */}
            <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 1.25, px: '22px', py: 1.5, borderTop: `1px solid ${tokens.line.default}`, bgcolor: tokens.surface.card, flexWrap: 'wrap' }}>
              {creating ? (
                <>
                  <Typography sx={{ flex: '1 1 240px', fontSize: 12.5, color: saveError ? tokens.signal.problem.ink : tokens.ink.bodyAlt }}>
                    {saveError || 'Brand and model are required. Everything else helps Motzu add it to the catalog sooner.'}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={requestClose}
                    disabled={submitMutation.isPending}
                    sx={{ height: 36, borderRadius: '9px', fontSize: 13.5, fontWeight: 600, color: tokens.ink.body, borderColor: tokens.line.default }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => submitMutation.mutate()}
                    disabled={!canSubmit}
                    sx={{ height: 36, borderRadius: '9px', fontSize: 13.5, fontWeight: 600 }}
                  >
                    {submitMutation.isPending ? 'Submitting…' : create?.submitLabel}
                  </Button>
                </>
              ) : editing ? (
                <>
                  <Dot tone={dirty ? 'caution' : 'neutral'} />
                  <Typography sx={{ flex: '1 1 240px', fontSize: 12.5, color: saveError ? tokens.signal.problem.ink : tokens.ink.bodyAlt }}>
                    {saveError ||
                      (dirty
                        ? submitter
                          ? 'Your changes go to your product right away and to Motzu’s review of your submission.'
                          : 'Saved fields are marked manual, so staged updates, price checks and enrichment won’t overwrite them.'
                        : 'No changes yet.')}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={discard}
                    disabled={saveMutation.isPending}
                    sx={{ height: 36, borderRadius: '9px', fontSize: 13.5, fontWeight: 600, color: tokens.ink.body, borderColor: tokens.line.default }}
                  >
                    Discard
                  </Button>
                  <Button
                    variant="contained"
                    onClick={requestSave}
                    disabled={saveMutation.isPending || !dirty}
                    sx={{ height: 36, borderRadius: '9px', fontSize: 13.5, fontWeight: 600 }}
                  >
                    {saveMutation.isPending ? 'Saving…' : 'Save changes'}
                  </Button>
                </>
              ) : (
                <>
                  <Typography sx={{ flex: '1 1 auto', fontSize: 12, color: tokens.ink.faint }}>
                    {[
                      product.createdAt && `Created ${formatDate(product.createdAt)}`,
                      product.promotedAt && `promoted ${formatDate(product.promotedAt)}`,
                      product.updatedAt && `updated ${formatDate(product.updatedAt)}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                  <Button onClick={requestClose} sx={{ height: 36, borderRadius: '9px', fontSize: 13.5, fontWeight: 600 }}>
                    Close
                  </Button>
                </>
              )}
            </Box>
          </>
        )}
      </Dialog>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this product?"
        message={
          <>
            <b>{product?.name}</b> and its media leave the catalog. Stores stop seeing it on their next sync. This can’t be undone.
          </>
        }
        cancelLabel="Keep product"
        confirmLabel="Delete product"
        confirmColor="error"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => setConfirmDelete(false)}
      />
      <ConfirmDialog
        open={mapConfirmOpen}
        title={mapConfirm?.to ? 'Change the stored MAP?' : 'Clear the stored MAP?'}
        message={
          <>
            MAP is {money(Number(mapConfirm?.from), currency)}
            {product?.pricing?.manufacturerPriceAsOf
              ? `, from the manufacturer price sheet applied ${formatDate(product.pricing.manufacturerPriceAsOf)}`
              : ', from the catalog'}
            . Stores use MAP as the advertised floor, so their next sync uses your value. It is saved as manual and kept through staged updates and price checks, but the next manufacturer price sheet for this brand can still replace it.
          </>
        }
        preview={
          <Box sx={{ mt: 1.5, px: 1.5, py: 1.25, borderRadius: `${tokens.radius.small}px`, bgcolor: tokens.surface.sunken, border: `1px solid ${tokens.line.default}`, fontVariantNumeric: 'tabular-nums' }}>
            <Typography component="span" sx={{ fontSize: 13, color: tokens.ink.faint, textDecoration: 'line-through' }}>{money(Number(mapConfirm?.from), currency)}</Typography>
            <Typography component="span" sx={{ fontSize: 13, color: tokens.ink.muted, mx: 1 }}>→</Typography>
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>{mapConfirm?.to ? money(Number(mapConfirm.to), currency) : 'No MAP'}</Typography>
          </Box>
        }
        cancelLabel="Keep current MAP"
        confirmLabel={mapConfirm?.to ? 'Change MAP' : 'Clear MAP'}
        confirmColor="warning"
        loading={saveMutation.isPending}
        onConfirm={() => {
          setMapConfirmOpen(false);
          saveMutation.mutate();
        }}
        onClose={() => setMapConfirmOpen(false)}
      />
      <ConfirmDialog
        open={confirmClose}
        title={creating ? 'Discard this product?' : 'Discard unsaved changes?'}
        message={creating ? 'What you entered has not been submitted.' : 'Your edits to this product have not been saved.'}
        cancelLabel="Keep editing"
        confirmLabel="Discard changes"
        confirmColor="error"
        onConfirm={() => {
          setConfirmClose(false);
          discard();
          onClose();
        }}
        onClose={() => setConfirmClose(false)}
      />
    </>
  );
};
